import type RiskScoringCompositorRepository from "@/core/administration/repositories/RiskScoringCompositorRepository";
import type {
  RiskScoringCompositorConfigPayload,
  RiskScoringCompositorCurrentConfig,
} from "@/core/administration/entities/RiskScoringCompositorConfig";
import type { RiskScoringCompositorHistoryEntry } from "@/core/administration/entities/RiskScoringCompositorHistory";
import { RepositoryResult } from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import {
  executeRequest,
  executeVoidRequest,
} from "../../http/services/executeRequest/executeRequest";
import { riskScoringCompositorEndpoints } from "../../api/riskScoringCompositorEndpoints/riskScoringCompositorEndpoints";

type CompositorConfigHistoryItemDto = {
  id: number;
  oldWeightTrackA: number;
  oldWeightTrackB: number;
  newWeightTrackA: number;
  newWeightTrackB: number;
  modifiedBy: string;
  reason: string;
  modifiedAt: string;
};

type CompositorConfigCurrentDto = {
  weightTrackA: number;
  weightTrackB: number;
  effectiveSince: string;
  lastModifiedBy: string;
  lastReason: string;
};

function mapCurrentConfigDto(
  dto: CompositorConfigCurrentDto,
): RiskScoringCompositorCurrentConfig {
  return {
    weightTrackA: dto.weightTrackA,
    weightTrackB: dto.weightTrackB,
    effectiveSince: dto.effectiveSince,
    lastModifiedBy: dto.lastModifiedBy,
    lastReason: dto.lastReason,
  };
}

function mapHistoryDto(
  dto: CompositorConfigHistoryItemDto,
): RiskScoringCompositorHistoryEntry {
  return {
    id: dto.id,
    oldWeightTrackA: dto.oldWeightTrackA,
    oldWeightTrackB: dto.oldWeightTrackB,
    newWeightTrackA: dto.newWeightTrackA,
    newWeightTrackB: dto.newWeightTrackB,
    modifiedBy: dto.modifiedBy,
    reason: dto.reason,
    modifiedAt: dto.modifiedAt,
  };
}

export default class RiskScoringCompositorRepositoryBackend implements RiskScoringCompositorRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async getCurrentConfig(): Promise<
    RepositoryResult<RiskScoringCompositorCurrentConfig>
  > {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<CompositorConfigCurrentDto>>({
          url: riskScoringCompositorEndpoints.config(),
          withAuth: true,
        }),
      mapCurrentConfigDto,
    );
  }

  async updateConfig(
    payload: RiskScoringCompositorConfigPayload,
  ): Promise<RepositoryResult<void>> {
    return executeVoidRequest(() =>
      this.httpClient.put<
        ApiResponse<unknown>,
        RiskScoringCompositorConfigPayload
      >({
        url: riskScoringCompositorEndpoints.config(),
        body: payload,
        withAuth: true,
      }),
    );
  }

  async getConfigHistory(): Promise<
    RepositoryResult<RiskScoringCompositorHistoryEntry[]>
  > {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<CompositorConfigHistoryItemDto[]>>({
          url: riskScoringCompositorEndpoints.configHistory(),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapHistoryDto),
    );
  }
}
